import { useState, useEffect } from 'react';
import { X, RefreshCw, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { getCommissionRate, brokerReportsCommissions } from '../../config';
import GlassSelect from '../Common/GlassSelect';

// Bulenox commission rates (All-in rates per side including Exchange, NFA, Order Routing, Commission)
// NOTE: These are still used as fallback; prefer config/commissionRates.js for most brokers
const BULENOX_RATES = {
  // CME Equity Indexes
  'EMD': 2.09, 'ES': 2.09, 'RTY': 2.09, 'NQ': 2.09,
  'MES': 0.61, 'MNQ': 0.61, 'M2K': 0.61,
  // Cryptocurrency
  'MBT': 2.76, 'MET': 0.46,
  // FX
  '6A': 2.36, 'M6A': 0.50, '6B': 2.36, 'M6B': 0.50,
  '6C': 2.36, '6E': 2.36, 'M6E': 0.50, '6J': 2.36,
  '6M': 2.36, '6N': 2.36, '6S': 2.36,
  // Agricultures
  'GF': 2.86, 'HE': 2.86, 'LE': 2.86,
  'ZL': 2.86, 'ZM': 2.86, 'ZS': 2.86, 'ZW': 2.86, 'ZO': 2.86,
  // NYMEX Energies
  'CL': 2.26, 'MCL': 0.76, 'NG': 2.26, 'QG': 1.26, 'QM': 1.96, 'RB': 2.26, 'PL': 2.31,
  // COMEX Metals
  'GC': 2.31, 'MGC': 0.76, 'HG': 2.31, 'SI': 2.31,
  // CBOT Indices
  'YM': 2.09, 'MYM': 0.61
};

const DEFAULT_ACCOUNT = {
  id: '',
  name: '',
  broker: '',
  type: 'Evaluation',
  status: 'Active',
  defaultSymbol: 'MNQ',
  strategy: '',
  startingBalance: 50000,
  dailyLossLimit: '',
  maxDrawdown: '',
  drawdownType: 'static',        // 'static', 'eodTrailing', 'liveTrailing'
  drawdownThreshold: '',         // Current threshold (editable for EOD trailing)
  commissionPerSide: 0.61,       // Default to Bulenox MNQ rate
  balanceAdjustment: 0,
  balanceLastSynced: null,
  // v7.6.2 — Per-account trading mode. Replaces the short-lived global
  // sessionMode pill. New trades on this account inherit this mode at
  // creation time as `trade.mode`. Coach analyzers (emotionalTilt today,
  // setup/ATM cohort splitters next) read trade.mode to filter or
  // downgrade behavioral observations on research-cadence work.
  //
  // Values: 'research' | 'production' | 'live' | 'automated' (future)
  // Auto-suggested from `type` if not set:
  //   Paper/Sim   → research
  //   Evaluation  → production
  //   Funded      → live
  //   Personal    → production
  mode: 'production',
};

// Suggested mode per account type — used as the initial value when creating
// a new account; user can override before saving. Existing accounts that
// already have a `mode` field set keep their explicit value.
export const SUGGESTED_MODE_FOR_TYPE = {
  'Paper': 'research',
  'Evaluation': 'production',
  'Funded': 'live',
  'Personal': 'production',
};

const AddEditAccountModal = ({
  darkMode,
  editingAccount,
  newAccount,
  setNewAccount,
  onClose,
  onSave,
  calculatedBalance = null
}) => {
  const [syncBalance, setSyncBalance] = useState('');
  const [syncSuccess, setSyncSuccess] = useState(false);
  
  // Auto-populate commission when broker, symbol, or data provider change
  useEffect(() => {
    if (newAccount.broker && newAccount.defaultSymbol) {
      // Try to get rate from centralized config
      const rate = getCommissionRate(newAccount.broker, newAccount.defaultSymbol, newAccount.dataProvider);
      if (rate !== null && rate !== newAccount.commissionPerSide) {
        setNewAccount(prev => ({ ...prev, commissionPerSide: rate }));
      } else if (newAccount.broker === 'Bulenox' && !rate) {
        // Fallback to local Bulenox rates if not in config
        const localRate = BULENOX_RATES[newAccount.defaultSymbol];
        if (localRate && localRate !== newAccount.commissionPerSide) {
          setNewAccount(prev => ({ ...prev, commissionPerSide: localRate }));
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newAccount.broker, newAccount.defaultSymbol, newAccount.dataProvider]);
  
  // No auto-calculation for threshold - user has full control
  // They can use the "Calculate" button if needed
  
  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    onSave();
  };
  
  const handleSyncBalance = () => {
    const actualBalance = parseFloat(syncBalance);
    if (isNaN(actualBalance) || calculatedBalance === null) return;
    
    // IMPORTANT: Use the ORIGINAL account's adjustment, not the form state
    // The calculatedBalance prop was calculated using editingAccount's adjustment,
    // so we must use that same value to get the correct base calculation
    const originalAdjustment = editingAccount?.balanceAdjustment || 0;
    const baseCalculated = calculatedBalance - originalAdjustment;
    const newAdjustment = actualBalance - baseCalculated;
    
    setNewAccount({
      ...newAccount,
      balanceAdjustment: newAdjustment,
      balanceLastSynced: new Date().toISOString()
    });
    
    setSyncSuccess(true);
    setTimeout(() => setSyncSuccess(false), 3000);
  };
  
  // Calculate current drawdown from threshold
  const getCurrentDrawdown = () => {
    if (!calculatedBalance || !newAccount.drawdownThreshold) return null;
    return calculatedBalance - parseFloat(newAccount.drawdownThreshold);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className={`rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto transition-colors duration-300 ${
        darkMode ? 'bg-gray-950/90 backdrop-blur-2xl border border-white/10' : 'bg-white'
      }`}>
        <div className="flex justify-between items-center mb-6">
          <h2 className={`text-xl font-bold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {editingAccount ? 'Edit Account' : 'Add New Account'}
          </h2>
          <button 
            onClick={handleClose}
            className={`p-2 rounded-lg transition-colors duration-300 ${
              darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Account ID */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Account ID *
            </label>
            <input
              type="text"
              placeholder="e.g., BX-42973-103"
              value={newAccount.id}
              onChange={(e) => setNewAccount({ ...newAccount, id: e.target.value })}
              disabled={editingAccount}
              className="w-full input-modern"
            />
          </div>

          {/* Account Name */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Display Name
            </label>
            <input
              type="text"
              placeholder="e.g., Bulenox 50K #1"
              value={newAccount.name}
              onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
              className="w-full input-modern"
            />
          </div>

          {/* Broker & Type Row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Broker
              </label>
              <GlassSelect
                value={newAccount.broker}
                onChange={(v) => setNewAccount({ ...newAccount, broker: v, dataProvider: '' })}
                options={[
                  { value: 'Bulenox', label: 'Bulenox' },
                  { value: 'Apex', label: 'Apex Trader Funding' },
                  { value: 'My Funded Futures', label: 'My Funded Futures' },
                  { value: 'Take Profit Trader', label: 'Take Profit Trader' },
                  { value: 'Tradeify', label: 'Tradeify' },
                  { value: 'Topstep', label: 'Topstep' },
                  { value: 'TradeDay', label: 'TradeDay' },
                  { value: 'Earn2Trade', label: 'Earn2Trade' },
                  { value: 'NinjaTrader', label: 'NinjaTrader' },
                  { value: 'Tradovate', label: 'Tradovate' },
                  { value: 'TDAmeritrade', label: 'TD Ameritrade' },
                  { value: 'Interactive Brokers', label: 'Interactive Brokers' },
                  { value: 'Other', label: 'Other' },
                ]}
                placeholder="Select Broker..."
                darkMode={darkMode}
                className="w-full"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Account Type
              </label>
              <GlassSelect
                value={newAccount.type}
                onChange={(v) => {
                  // Auto-suggest mode when type changes IF the user hasn't
                  // explicitly set a mode yet (or mode matches the prior type's
                  // default). This makes the upgrade migration zero-touch for
                  // most users: their existing account.type implies the right
                  // mode without them having to think about it.
                  const suggested = SUGGESTED_MODE_FOR_TYPE[v];
                  const priorSuggested = SUGGESTED_MODE_FOR_TYPE[newAccount.type];
                  const next = { ...newAccount, type: v };
                  if (suggested && (!newAccount.mode || newAccount.mode === priorSuggested)) {
                    next.mode = suggested;
                  }
                  setNewAccount(next);
                }}
                options={[
                  { value: 'Evaluation', label: 'Evaluation' },
                  { value: 'Funded', label: 'Funded' },
                  { value: 'Personal', label: 'Personal' },
                  { value: 'Paper', label: 'Paper/Sim' },
                ]}
                darkMode={darkMode}
                className="w-full"
              />
            </div>
          </div>

          {/* v7.6.2 — Trading mode per account. Replaces the short-lived
              global sessionMode pill. New trades on this account inherit
              this mode. Auto-suggested from Account Type (Paper→research,
              Eval→production, Funded→live, Personal→production) — manually
              overridable.

              Why this matters: multi-account prop traders routinely test on
              some accounts and run solid strategy on others simultaneously.
              A global mode pill couldn't handle that — switching modes for
              one account would mis-tag trades on the others. Per-account
              mode fits the actual workflow: each account has a long-term
              role (testing vs production vs funded), and the journal
              encodes that. Future automated-strategy work (NT Strategies
              attach per-account) extends this taxonomy with `automated`
              mode. */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Trading Mode
              <span className={`ml-2 text-xs font-normal ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                — tags new trades on this account; Coach analyzers split behavior by mode
              </span>
            </label>
            <GlassSelect
              value={newAccount.mode || 'production'}
              onChange={(v) => setNewAccount({ ...newAccount, mode: v })}
              options={[
                { value: 'research',   label: 'Research — small-size R&D, tilt capped at WARNING' },
                { value: 'production', label: 'Production — normal trading, full analysis' },
                { value: 'live',       label: 'Live — funded-money trading, full analysis + future weighting' },
              ]}
              darkMode={darkMode}
              className="w-full"
            />
          </div>

          {/* Data Provider Row - Only show for brokers that have multiple data providers */}
          {['Apex', 'Topstep', 'Earn2Trade'].includes(newAccount.broker) && (
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Data Provider
                <span className={`ml-2 text-xs ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                  (affects commission rates)
                </span>
              </label>
              <GlassSelect
                value={newAccount.dataProvider || ''}
                onChange={(v) => setNewAccount({ ...newAccount, dataProvider: v })}
                options={[
                  { value: 'Rithmic', label: 'Rithmic (NinjaTrader)' },
                  { value: 'Tradovate', label: 'Tradovate' },
                ]}
                placeholder="Select Data Provider..."
                darkMode={darkMode}
                className="w-full"
              />
            </div>
          )}

          {/* Status & Symbol Row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Status
              </label>
              <GlassSelect
                value={newAccount.status}
                onChange={(v) => setNewAccount({ ...newAccount, status: v })}
                options={[
                  { value: 'Active', label: 'Active' },
                  { value: 'Passed', label: 'Passed' },
                  { value: 'Blown', label: 'Blown' },
                  { value: 'Inactive', label: 'Inactive' },
                ]}
                darkMode={darkMode}
                className="w-full"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Default Symbol
              </label>
              <GlassSelect
                value={newAccount.defaultSymbol}
                onChange={(v) => setNewAccount({ ...newAccount, defaultSymbol: v })}
                options={[
                  { value: 'MNQ', label: 'MNQ - Micro Nasdaq', group: 'Equity Indexes' },
                  { value: 'MES', label: 'MES - Micro S&P', group: 'Equity Indexes' },
                  { value: 'M2K', label: 'M2K - Micro Russell', group: 'Equity Indexes' },
                  { value: 'MYM', label: 'MYM - Micro Dow', group: 'Equity Indexes' },
                  { value: 'NQ', label: 'NQ - E-mini Nasdaq', group: 'Equity Indexes' },
                  { value: 'ES', label: 'ES - E-mini S&P', group: 'Equity Indexes' },
                  { value: 'RTY', label: 'RTY - E-mini Russell', group: 'Equity Indexes' },
                  { value: 'YM', label: 'YM - E-mini Dow', group: 'Equity Indexes' },
                  { value: 'MCL', label: 'MCL - Micro Crude Oil', group: 'Energy' },
                  { value: 'CL', label: 'CL - Crude Oil', group: 'Energy' },
                  { value: 'NG', label: 'NG - Natural Gas', group: 'Energy' },
                  { value: 'MGC', label: 'MGC - Micro Gold', group: 'Metals' },
                  { value: 'GC', label: 'GC - Gold', group: 'Metals' },
                  { value: 'SI', label: 'SI - Silver', group: 'Metals' },
                ]}
                darkMode={darkMode}
                className="w-full"
              />
            </div>
          </div>

          {/* Strategy */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Strategy / Bot Name
            </label>
            <input
              type="text"
              placeholder="e.g., 5M 12pSL 1 Runner"
              value={newAccount.strategy}
              onChange={(e) => setNewAccount({ ...newAccount, strategy: e.target.value })}
              className="w-full input-modern"
            />
          </div>

          {/* Starting Balance */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Starting Balance
            </label>
            <input
              type="number"
              placeholder="50000"
              value={newAccount.startingBalance}
              onChange={(e) => setNewAccount({ ...newAccount, startingBalance: parseFloat(e.target.value) || 0 })}
              className="w-full input-modern"
            />
          </div>

          {/* Daily Loss Limit & Max Drawdown Row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Daily Loss Limit
              </label>
              <input
                type="number"
                placeholder="e.g., 1000"
                value={newAccount.dailyLossLimit}
                onChange={(e) => setNewAccount({ ...newAccount, dailyLossLimit: e.target.value })}
                className="w-full input-modern"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Max Drawdown Amount
              </label>
              <input
                type="number"
                placeholder="e.g., 2500"
                value={newAccount.maxDrawdown}
                onChange={(e) => setNewAccount({ ...newAccount, maxDrawdown: e.target.value })}
                className="w-full input-modern"
              />
            </div>
          </div>
          
          {/* Drawdown Type & Threshold */}
          <div className={`p-4 rounded-xl border ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            <h3 className={`font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <Info size={16} className="text-blue-500" />
              Drawdown Tracking
            </h3>
            
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Drawdown Type
                </label>
                <GlassSelect
                  value={newAccount.drawdownType || 'static'}
                  onChange={(v) => setNewAccount({ ...newAccount, drawdownType: v })}
                  options={[
                    { value: 'static', label: 'Static (Fixed)' },
                    { value: 'eodTrailing', label: 'EOD Trailing' },
                    { value: 'liveTrailing', label: 'Live Trailing' },
                  ]}
                  darkMode={darkMode}
                  className="w-full"
                />
              </div>
            </div>
            
            {/* Threshold - Full width row */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Drawdown Threshold
              </label>
              <div className="flex items-center gap-2">
                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>$</span>
                <input
                  type="number"
                  step="0.01"
                  placeholder={newAccount.startingBalance && newAccount.maxDrawdown 
                    ? `e.g., ${(parseFloat(newAccount.startingBalance) - parseFloat(newAccount.maxDrawdown)).toLocaleString()}`
                    : "e.g., 47500"
                  }
                  value={newAccount.drawdownThreshold || ''}
                  onChange={(e) => setNewAccount({ ...newAccount, drawdownThreshold: e.target.value })}
                  className="flex-1 input-modern"
                />
              </div>
              {newAccount.startingBalance && newAccount.maxDrawdown && !newAccount.drawdownThreshold && (
                <p className={`text-xs mt-1 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                  💡 Initial threshold would be ${(parseFloat(newAccount.startingBalance) - parseFloat(newAccount.maxDrawdown)).toLocaleString()} (Starting Balance - Max Drawdown)
                </p>
              )}
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                The balance level where your account would be blown. Set this to your broker’s actual threshold.
              </p>
            </div>
            
            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {newAccount.drawdownType === 'static' && 
                "Static: Threshold stays fixed at the value you set."}
              {newAccount.drawdownType === 'eodTrailing' && 
                "EOD Trailing: Auto-calculates from your highest EOD balance. Update manually if needed."}
              {newAccount.drawdownType === 'liveTrailing' && 
                "Live Trailing: Update this manually to match your broker's trailing threshold."}
            </p>
            
            {/* Show current cushion if editing */}
            {editingAccount && calculatedBalance && newAccount.drawdownThreshold && (
              <div className={`mt-3 p-2 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'}`}>
                <div className="flex justify-between items-center text-sm">
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                    Cushion to Threshold:
                  </span>
                  <span className={`font-bold ${
                    getCurrentDrawdown() > 0 ? 'text-green-500' : 'text-red-500'
                  }`}>
                    ${getCurrentDrawdown()?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Commission Per Side */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Commission Per Side (per contract)
            </label>
            <div className="flex items-center gap-2">
              <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>$</span>
              <input
                type="number"
                step="0.01"
                placeholder="0.61"
                value={newAccount.commissionPerSide || ''}
                onChange={(e) => setNewAccount({ ...newAccount, commissionPerSide: parseFloat(e.target.value) || 0 })}
                className="flex-1 input-modern"
              />
            </div>
            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {getCommissionRate(newAccount.broker, newAccount.defaultSymbol, newAccount.dataProvider) !== null ? (
                <>
                  <span className="text-green-500">✓ Auto-filled from {newAccount.broker} rates.</span>
                  {' '}Rate for {newAccount.defaultSymbol}: ${getCommissionRate(newAccount.broker, newAccount.defaultSymbol, newAccount.dataProvider)?.toFixed(2) || '?'}/side
                  {brokerReportsCommissions(newAccount.broker) && (
                    <span className="text-cyan-400 ml-1">(CSV imports include commissions)</span>
                  )}
                </>
              ) : newAccount.broker === 'Bulenox' && BULENOX_RATES[newAccount.defaultSymbol] ? (
                <>
                  <span className="text-green-500">✓ Auto-filled from Bulenox rates.</span>
                  {' '}All-in rate for {newAccount.defaultSymbol}: ${BULENOX_RATES[newAccount.defaultSymbol] || '?'}/side
                </>
              ) : (
                "Deducted from P&L calculations. Round-trip = 2 × per contract."
              )}
            </p>
          </div>
          
          {/* Balance Sync Section - Only show when editing an existing account */}
          {editingAccount && calculatedBalance !== null && (
            <div className={`mt-6 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-3">
                <RefreshCw size={18} className={darkMode ? 'text-cyan-400' : 'text-cyan-600'} />
                <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Sync Actual Account Balance
                </h3>
              </div>
              
              <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Adjust the balance to match your actual trading account.
              </p>
              
              {/* Current Journal Balance */}
              <div className={`p-3 rounded-lg mb-3 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Current Journal Balance:
                  </span>
                  <span className={`font-bold ${calculatedBalance >= (newAccount.startingBalance || 50000) ? 'text-green-500' : 'text-red-500'}`}>
                    ${calculatedBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                {/* Show the ORIGINAL adjustment that matches the calculatedBalance prop */}
                {editingAccount?.balanceAdjustment !== 0 && editingAccount?.balanceAdjustment !== undefined && (
                  <div className="flex justify-between items-center mt-1">
                    <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      (includes ${editingAccount.balanceAdjustment >= 0 ? '+' : ''}
                      {editingAccount.balanceAdjustment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} adjustment)
                    </span>
                  </div>
                )}
                {editingAccount?.balanceLastSynced && (
                  <div className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Last synced: {new Date(editingAccount.balanceLastSynced).toLocaleDateString()} at {new Date(editingAccount.balanceLastSynced).toLocaleTimeString()}
                  </div>
                )}
              </div>
              
              {/* Enter Actual Balance */}
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Actual Account Balance
                  </label>
                  <div className="flex items-center gap-2">
                    <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>$</span>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Enter actual balance..."
                      value={syncBalance}
                      onChange={(e) => setSyncBalance(e.target.value)}
                      className="flex-1 input-modern"
                    />
                  </div>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleSyncBalance}
                    disabled={!syncBalance || isNaN(parseFloat(syncBalance))}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      !syncBalance || isNaN(parseFloat(syncBalance))
                        ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                        : 'bg-cyan-600 text-white hover:bg-cyan-700'
                    }`}
                  >
                    <RefreshCw size={16} />
                    Sync
                  </button>
                </div>
              </div>
              
              {/* Sync Success Message */}
              {syncSuccess && (
                <div className={`mt-3 p-3 rounded-lg flex items-center gap-2 ${
                  darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                }`}>
                  <CheckCircle size={18} />
                  <span className="text-sm">Balance synced! Click “Save Changes” to apply.</span>
                </div>
              )}
              
              {/* Difference Preview */}
              {syncBalance && !isNaN(parseFloat(syncBalance)) && (
                <div className={`mt-3 p-3 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle size={16} className={darkMode ? 'text-yellow-400' : 'text-yellow-600'} />
                    <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Preview
                    </span>
                  </div>
                  <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {(() => {
                      const actual = parseFloat(syncBalance);
                      const diff = actual - calculatedBalance;
                      return (
                        <>
                          Difference: <span className={diff >= 0 ? 'text-green-500' : 'text-red-500'}>
                            {diff >= 0 ? '+' : '-'}${Math.abs(diff).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons — frosted-glass: neutral Cancel + blue Save CTA */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={handleClose}
            className={`px-4 py-2 rounded-lg border transition-[background-color,border-color,box-shadow,color] duration-150 ${
              darkMode
                ? 'bg-white/[0.04] border-white/10 text-gray-300 hover:bg-white/[0.08] hover:border-white/20 hover:text-white'
                : 'bg-white/60 border-gray-200 text-gray-700 hover:bg-white/80 hover:border-gray-300 hover:text-gray-900'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!newAccount.id.trim()}
            className={`px-4 py-2 rounded-lg border transition-[background-color,border-color,box-shadow,color] duration-150 disabled:opacity-40 disabled:cursor-not-allowed ${
              darkMode
                ? 'bg-blue-500/25 border-blue-400/50 text-blue-50 ring-1 ring-inset ring-blue-400/25 hover:bg-blue-500/35 hover:border-blue-400/70 hover:shadow-[0_0_18px_rgba(59,130,246,0.3)]'
                : 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200 hover:border-blue-400'
            }`}
          >
            {editingAccount ? 'Save Changes' : 'Add Account'}
          </button>
        </div>
      </div>
    </div>
  );
};

export { DEFAULT_ACCOUNT, BULENOX_RATES };
export default AddEditAccountModal;
